import React, { useState } from "react";
import {
  Check,
  Copy,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import {
  MoneyForbidden,
  Moneys,
  MoneyTick,
  MoneyTime,
} from "iconsax-reactjs";
import { toast } from "sonner";
import { TransactionItem } from "../../types";
import { CustomerAvatar } from "../../../otc-requests/components/CustomerAvatar";
import { ProviderBadge } from "../ProviderBadge";
import { TransactionStatusBadge } from "../TransactionStatusBadge";
import { CurrencyFlag } from "./CurrencyFlag";
import { formatCurrencyAmount } from "../../utils/formatCurrency";
import {
  formatTransactionStatus,
  isTerminalFailedStatus,
  normalizeTransactionStatus,
} from "../../utils/statusLookup";

interface TransactionOverviewCardProps {
  transaction: TransactionItem;
  status: string;
  isFlagged?: boolean;
  onRefreshStatus?: () => void;
  onViewWallet?: () => void;
}

const extractText = (val: any): string => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number") return String(val);
  if (typeof val === "object") {
    return val.value || val.name || "";
  }
  return "";
};

export const TransactionOverviewCard: React.FC<TransactionOverviewCardProps> = ({
  transaction,
  status,
  isFlagged,
  onRefreshStatus,
  onViewWallet,
}) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const customerName =
    extractText(transaction.customer?.name) ||
    extractText(transaction.business?.name) ||
    extractText(transaction.accountName) ||
    "N/A";

  const customerEmail =
    extractText(transaction.business?.email) ||
    extractText(transaction.customerEmail) ||
    "N/A";

  const currency = (transaction.currency || "USD").toUpperCase();

  const refCode =
    extractText(transaction.TransactionReference) ||
    extractText(transaction.RequestReference) ||
    extractText(transaction._id) ||
    "N/A";

  const method =
    extractText(transaction.channel) ||
    extractText(transaction.method) ||
    (transaction.TransactionType ? `${transaction.TransactionType} Settlement` : "N/A");

  const rawDate = transaction.transactionDate || transaction.createdAt || transaction.updatedAt;
  const formattedDate = rawDate
    ? new Date(rawDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "N/A";

  const formattedDateTime = rawDate
    ? `${new Date(rawDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })} • ${new Date(rawDate).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })}`
    : "N/A";

  const counterpartyName =
    extractText(transaction.counterparty) ||
    extractText(transaction.accountName) ||
    extractText(transaction.business?.name) ||
    "N/A";

  const accountNumber =
    extractText(transaction.accountNumber) ||
    extractText(transaction.bankAccountNumber) ||
    extractText(transaction.iban) ||
    "N/A";

  const bankName =
    extractText(transaction.bankName) ||
    extractText(transaction.bank) ||
    "N/A";

  const rawFee = transaction.fee ?? transaction.transactionFee;
  const feeDisplay =
    rawFee !== undefined && rawFee !== null && rawFee !== ""
      ? formatCurrencyAmount(rawFee, currency)
      : "N/A";

  const providerName = extractText(transaction.provider) || "N/A";
  const referenceId = extractText(transaction.referenceId) || refCode;
  const remark =
    extractText(transaction.narration) ||
    extractText(transaction.reason) ||
    extractText(transaction.remark) ||
    "N/A";
  const walletBalanceDisplay = transaction.walletBalance || "N/A";

  const normalizedStatus = normalizeTransactionStatus(status);
  const isFailed = isTerminalFailedStatus(status);

  return (
    <div className="rounded-2xl border border-[#EEF1FB] bg-white p-6 shadow-sm">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_1fr_0.9fr] lg:gap-0">
        {/* Column 1: Customer & Transfer Details */}
        <div className="flex flex-col lg:pr-4">
          {/* Customer info header */}
          <div className="flex h-[40px] items-center gap-3">
            <CustomerAvatar name={customerName} size={36} />
            <div className="flex min-w-0 flex-col">
              <span
                className="truncate text-[14px] font-semibold text-[#101828]"
                title={customerName}
              >
                {customerName}
              </span>
              <span
                className="truncate text-[12px] text-[#667085]"
                title={customerEmail}
              >
                {customerEmail}
              </span>
            </div>
          </div>

          {/* Wallet balance with border top AND bottom */}
          <div className="my-3.5 flex items-center justify-between border-y border-[#F2F4F7] py-3.5 text-[13px]">
            <div className="flex items-center gap-2 text-[#475467]">
              <CurrencyFlag currency={currency} />
              <span>Wallet balance</span>
            </div>
            <div className="flex items-center gap-1.5 font-semibold text-[#101828]">
              <span>{walletBalanceDisplay}</span>
              <button
                type="button"
                onClick={onViewWallet}
                className="rounded p-0.5 text-[#6F00FF] transition-colors hover:text-[#5800CC]"
                title="View wallet"
              >
                <ExternalLink size={13} />
              </button>
            </div>
          </div>

          {/* Detail rows below divider with increased gap */}
          <div className="flex flex-col gap-4 text-[13px]">
            {/* Reference */}
            <div className="flex items-center justify-between">
              <span className="text-[#667085]">Reference</span>
              <div className="flex items-center gap-1.5 font-medium text-[#101828]">
                <span
                  className="max-w-[140px] truncate font-mono text-[12px] sm:max-w-[180px]"
                  title={refCode}
                >
                  {refCode}
                </span>
                <button
                  type="button"
                  onClick={() => handleCopy(refCode, "ref")}
                  className="rounded p-0.5 text-[#667085] transition-colors hover:text-[#101828]"
                  title="Copy reference"
                >
                  {copiedKey === "ref" ? (
                    <Check size={13} className="text-[#12B76A]" />
                  ) : (
                    <Copy size={13} />
                  )}
                </button>
              </div>
            </div>

            {/* Method */}
            <div className="flex items-center justify-between">
              <span className="text-[#667085]">Method</span>
              <span className="max-w-[180px] truncate font-medium text-[#101828]" title={method}>
                {method}
              </span>
            </div>

            {/* Date */}
            <div className="flex items-center justify-between">
              <span className="text-[#667085]">Date</span>
              <span className="font-medium text-[#101828]">{formattedDate}</span>
            </div>

            {/* Sender / Receiver */}
            <div className="flex items-start justify-between">
              <span className="text-[#667085]">
                {transaction.PaymentType === "debit" ? "Receiver" : "Sender/Receiver"}
              </span>
              <div className="flex flex-col items-end text-right">
                <span
                  className="max-w-[160px] truncate font-medium text-[#101828]"
                  title={counterpartyName}
                >
                  {counterpartyName}
                </span>
                <span
                  className="max-w-[180px] truncate text-[12px] text-[#667085]"
                  title={accountNumber !== "N/A" && bankName !== "N/A" ? `${accountNumber} • ${bankName}` : accountNumber !== "N/A" ? accountNumber : bankName}
                >
                  {accountNumber !== "N/A" && bankName !== "N/A" ? `${accountNumber} • ${bankName}` : accountNumber !== "N/A" ? accountNumber : bankName}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Column 2: Other Details */}
        <div className="flex flex-col border-t border-[#F2F4F7] pt-6 lg:border-l lg:border-t-0 lg:px-4 lg:pt-0">
          {/* Header */}
          <div className="flex h-[40px] items-center">
            <h2 className="text-[14px] font-semibold text-[#101828]">Other Details</h2>
          </div>

          {/* Fee with border top AND bottom */}
          <div className="my-3.5 flex items-center justify-between border-y border-[#F2F4F7] py-3.5 text-[13px]">
            <span className="text-[#667085]">Fee</span>
            <span className="font-medium text-[#101828]">{feeDisplay}</span>
          </div>

          {/* Detail rows below divider with increased gap */}
          <div className="flex flex-col gap-4 text-[13px]">
            {/* Provider */}
            <div className="flex items-center justify-between">
              <span className="text-[#667085]">Provider</span>
              <ProviderBadge provider={providerName} />
            </div>

            {/* Status */}
            <div className="flex items-start justify-between">
              <span className="text-[#667085]">Status</span>
              <div className="flex flex-col items-end gap-0.5">
                <div className="flex items-center gap-1.5">
                  <TransactionStatusBadge
                    status={isFlagged ? "Flagged" : status}
                  />
                  {onRefreshStatus && (
                    <button
                      type="button"
                      onClick={onRefreshStatus}
                      className="rounded p-0.5 text-[#667085] transition-colors hover:text-[#101828]"
                      title="Refresh status"
                    >
                      <RefreshCw size={13} />
                    </button>
                  )}
                </div>
                <span className="text-[11px] italic text-[#667085]">
                  Last updated: {formattedDate}
                </span>
              </div>
            </div>

            {/* Reference ID */}
            <div className="flex items-center justify-between">
              <span className="text-[#667085]">Reference</span>
              <span
                className="max-w-[140px] truncate font-mono text-[12px] font-medium text-[#101828] sm:max-w-[180px]"
                title={referenceId}
              >
                {referenceId}
              </span>
            </div>

            {/* Remark */}
            <div className="flex items-center justify-between">
              <span className="text-[#667085]">Remark</span>
              <span
                className="max-w-[160px] truncate font-medium text-[#101828] sm:max-w-[180px]"
                title={remark}
              >
                {remark}
              </span>
            </div>
          </div>
        </div>

        {/* Column 3: Status Stepper */}
        <div className="flex flex-col border-t border-[#F2F4F7] pt-6 lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0">
          <div className="flex h-[40px] items-center">
            <h2 className="text-[14px] font-semibold text-[#101828]">Status</h2>
          </div>

          <div className="mt-3.5 flex flex-col pt-1">
            {/* Step 1: Completed / Failed / Flagged (money-tick) */}
            <div className="relative flex items-start gap-3 pb-7">
              {/* Connecting vertical line to Step 2 */}
              <div className="absolute left-[6px] top-[18px] bottom-0.5 w-[2px] bg-[#EAECF0]" />
              <div className="z-10 mt-0.5 shrink-0">
                {isFlagged ? (
                  <MoneyTick size={14} variant="Bold" color="#F79009" />
                ) : isFailed ? (
                  <MoneyForbidden size={14} variant="Bold" color="#D92D20" />
                ) : (
                  <MoneyTick size={14} variant="Bold" color="#12B76A" />
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-[13px] font-semibold text-[#101828]">
                  {isFlagged
                    ? "Transaction flagged"
                    : isFailed
                      ? `Transaction ${formatTransactionStatus(status).toLowerCase()}`
                      : normalizedStatus === "under_review"
                        ? "Under review"
                        : normalizedStatus === "pending"
                          ? "Transaction pending"
                          : normalizedStatus === "awaiting_funding"
                            ? "Awaiting funding"
                            : normalizedStatus === "offer_sent"
                              ? "Offer sent"
                              : normalizedStatus === "offer_accepted"
                                ? "Offer accepted"
                                : normalizedStatus === "agreed"
                                  ? "Agreement reached"
                                  : normalizedStatus === "request_approved"
                                    ? "Request approved"
                                    : "Transaction completed"}
                </span>
                <span className="text-[11px] text-[#667085]">
                  {formattedDateTime}
                </span>
              </div>
            </div>

            {/* Step 2: Processing (money-time) */}
            <div className="relative flex items-start gap-3 pb-7">
              {/* Connecting vertical line to Step 3 */}
              <div className="absolute left-[6px] top-[18px] bottom-0.5 w-[2px] bg-[#EAECF0]" />
              <div className="z-10 mt-0.5 shrink-0">
                <MoneyTime size={14} variant="Bold" color="#6F00FF" />
              </div>
              <div className="flex flex-col">
                <span className="text-[13px] font-medium text-[#344054]">
                  Transaction processing
                </span>
                <span className="text-[11px] text-[#667085]">
                  {formattedDateTime}
                </span>
              </div>
            </div>

            {/* Step 3: Initiated (moneys) */}
            <div className="relative flex items-start gap-3">
              <div className="z-10 mt-0.5 shrink-0">
                <Moneys size={14} variant="Bold" color="#344054" />
              </div>
              <div className="flex flex-col">
                <span className="text-[13px] font-medium text-[#344054]">
                  Transaction initiated
                </span>
                <span className="text-[11px] text-[#667085]">
                  {formattedDateTime}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
